import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../features/auth/AuthContext';
import { fetchActiveAds } from '../../features/ads/api/ads';
import type { Ad } from '../../types';
import { TopbarAd } from './TopbarAd';
import { PopupAd } from './PopupAd';

function resolveAudience(user: ReturnType<typeof useAuth>['user']): 'all' | 'member' | 'vip' {
  if (!user) return 'all';
  const staff = ['admin', 'mentor', 'sales'];
  if (user.roles?.some((r) => staff.includes(r))) return 'all';
  if (user.is_vip || user.vip_status === 'active' || user.roles?.includes('vip')) return 'vip';
  if (user.roles?.includes('member')) return 'member';
  return 'all';
}

export function AdRenderer() {
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes('admin');
  const audience = useMemo(() => resolveAudience(user), [user]);
  const [ads, setAds] = useState<Ad[]>([]);

  useEffect(() => {
    if (isAdmin) { setAds([]); return; }
    let alive = true;
    fetchActiveAds(audience)
      .then((data) => { if (alive) setAds(data); })
      .catch(() => { if (alive) setAds([]); });
    return () => { alive = false; };
  }, [audience, isAdmin]);

  const pick = (t: string) => ads.filter((a) => a.type === t).sort((a, b) => (b.priority || 0) - (a.priority || 0))[0];
  const topbar = pick('topbar');
  const popup = pick('popup');

  if (isAdmin) return null;

  return (
    <>
      {topbar && <TopbarAd ad={topbar} />}
      {popup && <PopupAd ad={popup} />}
    </>
  );
}
